import { theme } from '@weave/theme-original';
import { PrimaryButton, Text } from '@weave/design-system';
import { CopyToClipboardButton } from 'components/shared/copy-to-clipboard/copy-to-clipboard-button';
import React, { useState } from 'react';
import { UserLocations } from './user-locations.component';
import { UserLookupCard } from './user-lookup-card';
import { SearchUserByEmailResponse, SearchUserByIdResponse } from './user-search.api';
import { BulkManageModal } from './bulk-manage-modal';
import { hasAccess } from '../../helpers/authz';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { requestState } from './user-search.api';
import { useResetUser } from '../../apis/auth/mutations';
import { useGetContacts, useGetUserDetails } from '../../apis/auth/queries';
import { DestructiveConfirmationModal } from '../shared/confirmation-modal/destructive-confirmation-modal.component';
import { useAlert } from '@weave/alert-system';
import { getErrorMessage } from '../../redux/axios';

type UserLookupDetailsProps = {
  userData: SearchUserByIdResponse | SearchUserByEmailResponse[0];
};

export const UserLookupDetails = ({ userData }: UserLookupDetailsProps) => {
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const alerts = useAlert();

  const { data: userDetails, isError: isMfaError } = useGetUserDetails(userData.UserID);
  const {
    data: contacts,
    isLoading: isContactsLoading,
    isError: isContactsError,
  } = useGetContacts(userData.UserID);
  const resetUserMutation = useResetUser({
    onSuccess: () => {
      alerts.success('User reset successfully');
      setIsResetConfirmOpen(false);
    },
    onError: (err) => {
      alerts.error(String(getErrorMessage(err) || 'Failed to reset user'));
      setIsResetConfirmOpen(false);
    },
  });

  const handleBulkSuccess = () => {
    requestState.searchTerm$.next(requestState.searchTerm$.value);
  };

  return (
    <>
      <UserLookupCard
        css={{ marginBottom: theme.spacing(2) }}
        as="dl"
        aria-label="User Search Result"
      >
        {Object.entries(userData).map(
          ([key, value]) =>
            value && (
              <React.Fragment key={key}>
                <dt>{key}</dt>
                <dd>
                  <CopyToClipboardButton>{value}</CopyToClipboardButton>
                </dd>
              </React.Fragment>
            )
        )}
        {hasAccess(Permission.WEAVE_USER_BULK_ADMIN) && (
          <>
            <dt>Bulk Actions</dt>
            <dd>
              <PrimaryButton
                onClick={() => setIsBulkModalOpen(true)}
                style={{ width: 'auto' }}
              >
                Bulk Manage Access
              </PrimaryButton>
            </dd>
          </>
        )}
        {hasAccess(Permission.USER_RESET) && (
          <>
            <dt>Reset User</dt>
            <dd>
              <PrimaryButton
                onClick={() => setIsResetConfirmOpen(true)}
                style={{ width: 'auto' }}
              >
                Reset User
              </PrimaryButton>
            </dd>
          </>
        )}
        <dt>MFA Status</dt>
        <dd>
          <Text>
            {isMfaError
              ? 'Unable to load MFA status'
              : userDetails?.mfaEnabled
              ? 'Enabled'
              : 'Disabled'}
          </Text>
        </dd>
        <dt>Contacts</dt>
        <dd>
          {isContactsLoading && <Text>Loading contacts...</Text>}
          {isContactsError && <Text>Unable to load contacts.</Text>}
          {!isContactsLoading && !isContactsError && (
            <>
              {contacts && contacts.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: theme.spacing(2) }}>
                  {contacts.map((contact) => (
                    <li key={contact.contactId}>
                      <div>{contact.contact}</div>
                      <small>{contact.state}</small>
                    </li>
                  ))}
                </ul>
              ) : (
                <Text>No contacts found.</Text>
              )}
            </>
          )}
        </dd>
        <dt>Locations</dt>
        <dd>
          <UserLocations userId={userData.UserID} />
        </dd>
      </UserLookupCard>

      {hasAccess(Permission.WEAVE_USER_BULK_ADMIN) && (
        <BulkManageModal
          isOpen={isBulkModalOpen}
          onClose={() => setIsBulkModalOpen(false)}
          userId={userData.UserID}
          onSuccess={handleBulkSuccess}
        />
      )}

      {hasAccess(Permission.USER_RESET) && (
        <DestructiveConfirmationModal
          show={isResetConfirmOpen}
          onClose={() => setIsResetConfirmOpen(false)}
          onConfirm={() => resetUserMutation.mutate(userData.UserID)}
          title="Reset User"
          message={`Are you sure you want to reset ${
            userData.Username || userData.UserID
          }? This will force the user to reset their password and invalidate all current active login tokens.`}
          confirmLabel="Reset User"
          loading={resetUserMutation.isLoading}
          loadingLabel="Resetting..."
        />
      )}
    </>
  );
};
